import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Users,
  AlertTriangle,
  ClipboardList,
  MessageSquare,
  RefreshCw,
  Activity,
  Heart,
  User,
  FileText,
  Clock,
} from 'lucide-react';
import { dashboardApi } from '@/api/endpoints/dashboard.api';
import type { AlertResponse } from '@/types/alert.types';

export default function DashboardPage() {
  const navigate = useNavigate();

  // Use React Query to leverage prefetched data from usePrefetchDashboardData
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['dashboard', 'all'],
    queryFn: () => dashboardApi.getAll(),
    staleTime: 1000 * 60 * 5, // 5 minutes (matches backend cache)
    refetchOnWindowFocus: false,
  });

  const summary = data?.summary;
  const riskDistribution = data?.riskDistribution;
  const alertsSummary = data?.alertsSummary;
  const careLineDistribution = data?.careLineDistribution;
  const patientsAtRisk = data?.patientsAtRisk;
  const recentActivities = data?.recentActivities;

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Carregando dashboard...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4">
        <p className="text-red-800">Erro ao carregar dados do dashboard. Tente novamente.</p>
        <button
          onClick={() => refetch()}
          className="mt-2 rounded bg-red-600 px-4 py-2 text-white hover:bg-red-700"
        >
          Tentar novamente
        </button>
      </div>
    );
  }

  const totalRisk = riskDistribution?.total || 1;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Dashboard</h1>
          <p className="text-muted-foreground">Visão geral da carteira de pacientes</p>
        </div>
        <button
          onClick={() => refetch()}
          className="flex items-center gap-2 rounded-lg border px-3 py-2 text-sm hover:bg-muted"
        >
          <RefreshCw className="h-4 w-4" />
          Atualizar
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Pacientes Ativos"
          value={summary?.totalPatients?.toString() || '0'}
          icon={Users}
          onClick={() => navigate('/patients')}
        />
        <StatCard
          title="Alto Risco"
          value={((summary?.patientsHighRisk || 0) + (summary?.patientsCriticalRisk || 0)).toString()}
          icon={AlertTriangle}
          iconColor="text-red-500"
          onClick={() => navigate('/patients?riskLevel=HIGH,CRITICAL')}
        />
        <StatCard
          title="Questionários Pendentes"
          value={summary?.pendingQuestionnaires?.toString() || '0'}
          icon={ClipboardList}
          onClick={() => navigate('/questionnaires?status=SENT,DELIVERED')}
        />
        <StatCard
          title="Demandas Abertas"
          value={summary?.openDemands?.toString() || '0'}
          icon={MessageSquare}
          onClick={() => navigate('/demands')}
        />
      </div>

      {/* Risk Distribution and Alerts */}
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-lg border bg-card p-6">
          <h2 className="mb-4 text-lg font-semibold">Distribuição por Risco</h2>
          <div className="space-y-3">
            <RiskBar
              label="Baixo"
              count={riskDistribution?.low || 0}
              percentage={Math.round(((riskDistribution?.low || 0) / totalRisk) * 100)}
              color="bg-green-500"
              onClick={() => navigate('/patients?riskLevel=LOW')}
            />
            <RiskBar
              label="Moderado"
              count={riskDistribution?.moderate || 0}
              percentage={Math.round(((riskDistribution?.moderate || 0) / totalRisk) * 100)}
              color="bg-yellow-500"
              onClick={() => navigate('/patients?riskLevel=MODERATE')}
            />
            <RiskBar
              label="Alto"
              count={riskDistribution?.high || 0}
              percentage={Math.round(((riskDistribution?.high || 0) / totalRisk) * 100)}
              color="bg-red-500"
              onClick={() => navigate('/patients?riskLevel=HIGH')}
            />
            <RiskBar
              label="Crítico"
              count={riskDistribution?.critical || 0}
              percentage={Math.round(((riskDistribution?.critical || 0) / totalRisk) * 100)}
              color="bg-purple-500"
              onClick={() => navigate('/patients?riskLevel=CRITICAL')}
            />
          </div>
        </div>

        <div className="rounded-lg border bg-card p-6">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold">Alertas Recentes</h2>
            {alertsSummary && alertsSummary.totalUnread > 0 && (
              <span className="rounded-full bg-red-100 px-2 py-1 text-xs font-medium text-red-800">
                {alertsSummary.totalUnread} não lidos
              </span>
            )}
          </div>
          <div className="space-y-3">
            {alertsSummary?.alerts && alertsSummary.alerts.length > 0 ? (
              alertsSummary.alerts.map((alert) => (
                <AlertItem
                  key={alert.id}
                  alert={alert}
                  onClick={() => navigate(`/questionnaires/responses/${alert.sourceId}`)}
                />
              ))
            ) : (
              <p className="py-4 text-center text-muted-foreground">Nenhum alerta recente</p>
            )}
          </div>
        </div>
      </div>

      {/* Quick Stats Row */}
      <div className="grid gap-4 md:grid-cols-3">
        <QuickStatCard
          title="Tarefas Pendentes"
          value={summary?.pendingTasks || 0}
          subtitle={`${summary?.overdueTasks || 0} atrasadas`}
          onClick={() => navigate('/tasks')}
        />
        <QuickStatCard
          title="Demandas Urgentes"
          value={summary?.urgentDemands || 0}
          subtitle={`${summary?.unassignedDemands || 0} não atribuídas`}
          onClick={() => navigate('/demands?urgency=HIGH')}
        />
        <QuickStatCard
          title="Mensagens"
          value={summary?.unreadMessages || 0}
          subtitle={`${summary?.urgentMessages || 0} urgentes`}
          onClick={() => navigate('/messages')}
        />
      </div>

      {/* Care Line Distribution and Patients at Risk */}
      <div className="grid gap-4 lg:grid-cols-2">
        {/* Care Line Distribution */}
        <div className="rounded-lg border bg-card p-6">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="flex items-center gap-2 text-lg font-semibold">
              <Heart className="h-5 w-5 text-primary" />
              Distribuição por Linha de Cuidado
            </h2>
            <span className="text-sm text-muted-foreground">
              {careLineDistribution?.total || 0} pacientes
            </span>
          </div>
          <div className="space-y-3">
            {careLineDistribution?.distributions && careLineDistribution.distributions.length > 0 ? (
              careLineDistribution.distributions.map((item) => (
                <CareLineBar
                  key={item.careLineId}
                  name={item.careLineName}
                  count={item.patientCount}
                  total={careLineDistribution.total}
                  onClick={() => navigate(`/care-lines/${item.careLineId}`)}
                />
              ))
            ) : (
              <p className="py-4 text-center text-muted-foreground">Nenhuma linha de cuidado configurada</p>
            )}
          </div>
        </div>

        {/* Patients at Risk */}
        <div className="rounded-lg border bg-card p-6">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="flex items-center gap-2 text-lg font-semibold">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Pacientes de Alto Risco
            </h2>
            <button
              onClick={() => navigate('/patients?riskLevel=HIGH,CRITICAL')}
              className="text-sm text-primary hover:underline"
            >
              Ver todos
            </button>
          </div>
          <div className="space-y-3">
            {patientsAtRisk?.patients && patientsAtRisk.patients.length > 0 ? (
              patientsAtRisk.patients.map((patient) => (
                <PatientAtRiskItem
                  key={patient.id}
                  patient={patient}
                  onClick={() => navigate(`/patients/${patient.id}`)}
                />
              ))
            ) : (
              <p className="py-4 text-center text-muted-foreground">Nenhum paciente de alto risco</p>
            )}
          </div>
        </div>
      </div>

      {/* Expired Assessments */}
      {patientsAtRisk?.expiredAssessments && patientsAtRisk.expiredAssessments.length > 0 && (
        <div className="rounded-lg border border-orange-200 bg-orange-50 p-6">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="flex items-center gap-2 text-lg font-semibold text-orange-800">
              <Clock className="h-5 w-5 text-orange-600" />
              Avaliações Vencidas
            </h2>
            <span className="rounded-full bg-orange-200 px-2 py-1 text-xs font-medium text-orange-800">
              {patientsAtRisk.totalExpiredAssessments} paciente{patientsAtRisk.totalExpiredAssessments !== 1 ? 's' : ''}
            </span>
          </div>
          <p className="mb-4 text-sm text-orange-700">
            Pacientes de alto/crítico risco que precisam ser reavaliados
          </p>
          <div className="grid gap-3 md:grid-cols-2">
            {patientsAtRisk.expiredAssessments.map((patient) => (
              <ExpiredAssessmentItem
                key={patient.id}
                patient={patient}
                onClick={() => navigate(`/patients/${patient.id}`)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Recent Activities */}
      <div className="rounded-lg border bg-card p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-lg font-semibold">
            <Activity className="h-5 w-5 text-primary" />
            Atividades Recentes
          </h2>
        </div>
        <div className="space-y-3">
          {recentActivities?.activities && recentActivities.activities.length > 0 ? (
            recentActivities.activities.map((activity) => (
              <ActivityItem
                key={activity.id}
                activity={activity}
                onClick={() => {
                  if (activity.type === 'QUESTIONNAIRE_ANSWERED') {
                    navigate(`/questionnaires/responses/${activity.id}`);
                  } else if (activity.type === 'DEMAND_RESOLVED' && activity.patientId) {
                    navigate(`/demands?patientId=${activity.patientId}`);
                  } else if (activity.patientId) {
                    navigate(`/patients/${activity.patientId}`);
                  }
                }}
              />
            ))
          ) : (
            <p className="py-4 text-center text-muted-foreground">Nenhuma atividade recente</p>
          )}
        </div>
      </div>
    </div>
  );
}

interface StatCardProps {
  title: string;
  value: string;
  icon: React.ElementType;
  iconColor?: string;
  onClick?: () => void;
}

function StatCard({ title, value, icon: Icon, iconColor, onClick }: StatCardProps) {
  return (
    <div
      className={`rounded-lg border bg-card p-6 transition-colors ${onClick ? 'cursor-pointer hover:bg-muted/50' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-muted-foreground">{title}</p>
          <p className="text-2xl font-bold">{value}</p>
        </div>
        <Icon className={`h-8 w-8 ${iconColor || 'text-primary'}`} />
      </div>
    </div>
  );
}

interface RiskBarProps {
  label: string;
  count: number;
  percentage: number;
  color: string;
  onClick?: () => void;
}

function RiskBar({ label, count, percentage, color, onClick }: RiskBarProps) {
  return (
    <div
      className={`${onClick ? 'cursor-pointer hover:opacity-80' : ''}`}
      onClick={onClick}
    >
      <div className="mb-1 flex justify-between text-sm">
        <span>{label}</span>
        <span className="text-muted-foreground">{count} ({percentage}%)</span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-muted">
        <div className={`h-full ${color} transition-all`} style={{ width: `${Math.max(percentage, 2)}%` }} />
      </div>
    </div>
  );
}

interface AlertItemProps {
  alert: AlertResponse;
  onClick?: () => void;
}

function AlertItem({ alert, onClick }: AlertItemProps) {
  const colors: Record<string, string> = {
    CRITICAL: 'border-l-purple-500 bg-purple-50',
    HIGH: 'border-l-red-500 bg-red-50',
    MODERATE: 'border-l-yellow-500 bg-yellow-50',
    LOW: 'border-l-blue-500 bg-blue-50',
  };

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 60) return `Há ${diffMins} min`;
    if (diffHours < 24) return `Há ${diffHours} hora${diffHours > 1 ? 's' : ''}`;
    return `Há ${diffDays} dia${diffDays > 1 ? 's' : ''}`;
  };

  const isPending = alert.status === 'PENDING';

  return (
    <div
      className={`rounded-r-lg border-l-4 p-3 ${colors[alert.severity] || 'border-l-gray-500 bg-gray-50'} ${onClick ? 'cursor-pointer hover:opacity-80' : ''} ${isPending ? 'font-medium' : ''}`}
      onClick={onClick}
    >
      <p className="font-medium">{alert.title}</p>
      <p className="text-sm text-muted-foreground">{alert.patientName} • {formatTime(alert.createdAt)}</p>
    </div>
  );
}

interface QuickStatCardProps {
  title: string;
  value: number;
  subtitle: string;
  onClick?: () => void;
}

function QuickStatCard({ title, value, subtitle, onClick }: QuickStatCardProps) {
  return (
    <div
      className={`rounded-lg border bg-card p-4 ${onClick ? 'cursor-pointer hover:bg-muted/50' : ''}`}
      onClick={onClick}
    >
      <p className="text-sm text-muted-foreground">{title}</p>
      <p className="text-xl font-bold">{value}</p>
      <p className="text-xs text-muted-foreground">{subtitle}</p>
    </div>
  );
}

interface CareLineBarProps {
  name: string;
  count: number;
  total: number;
  onClick?: () => void;
}

function CareLineBar({ name, count, total, onClick }: CareLineBarProps) {
  const percentage = total > 0 ? Math.round((count / total) * 100) : 0;

  return (
    <div
      className={`${onClick ? 'cursor-pointer hover:opacity-80' : ''}`}
      onClick={onClick}
    >
      <div className="mb-1 flex justify-between text-sm">
        <span>{name}</span>
        <span className="text-muted-foreground">{count} ({percentage}%)</span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-muted">
        <div
          className="h-full bg-primary transition-all"
          style={{ width: `${Math.max(percentage, 2)}%` }}
        />
      </div>
    </div>
  );
}

interface PatientAtRiskItemProps {
  patient: {
    id: string;
    name: string;
    age: number;
    riskLevel: 'HIGH' | 'CRITICAL';
    riskScore: number;
    mainProblems: string[];
  };
  onClick?: () => void;
}

function PatientAtRiskItem({ patient, onClick }: PatientAtRiskItemProps) {
  const riskColors = {
    HIGH: 'border-l-red-500 bg-red-50',
    CRITICAL: 'border-l-purple-500 bg-purple-50',
  };

  const riskLabels = {
    HIGH: 'Alto',
    CRITICAL: 'Crítico',
  };

  return (
    <div
      className={`rounded-r-lg border-l-4 p-3 ${riskColors[patient.riskLevel]} ${onClick ? 'cursor-pointer hover:opacity-80' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <User className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">{patient.name}</span>
          <span className="text-sm text-muted-foreground">({patient.age} anos)</span>
        </div>
        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${
          patient.riskLevel === 'CRITICAL' ? 'bg-purple-100 text-purple-800' : 'bg-red-100 text-red-800'
        }`}>
          {riskLabels[patient.riskLevel]} ({patient.riskScore})
        </span>
      </div>
      {patient.mainProblems && patient.mainProblems.length > 0 && (
        <p className="mt-1 text-sm text-muted-foreground">
          {patient.mainProblems.slice(0, 2).join(', ')}
          {patient.mainProblems.length > 2 && ` +${patient.mainProblems.length - 2}`}
        </p>
      )}
    </div>
  );
}

interface ExpiredAssessmentItemProps {
  patient: {
    id: string;
    name: string;
    age: number;
    riskLevel: 'HIGH' | 'CRITICAL';
    riskScore: number;
    mainProblems: string[];
    lastAssessmentAt: string | null;
    expiredAt?: string | null;
  };
  onClick?: () => void;
}

function ExpiredAssessmentItem({ patient, onClick }: ExpiredAssessmentItemProps) {
  const formatExpiredDays = (dateStr: string | null | undefined) => {
    if (!dateStr) return 'Vencida';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Venceu hoje';
    if (diffDays === 1) return 'Venceu ontem';
    return `Vencida há ${diffDays} dias`;
  };

  const riskLabels = {
    HIGH: 'Alto',
    CRITICAL: 'Crítico',
  };

  return (
    <div
      className={`rounded-lg border border-orange-300 bg-white p-3 ${onClick ? 'cursor-pointer hover:bg-orange-50' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <User className="h-4 w-4 text-orange-600" />
          <span className="font-medium text-orange-900">{patient.name}</span>
          <span className="text-sm text-orange-700">({patient.age} anos)</span>
        </div>
        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${
          patient.riskLevel === 'CRITICAL' ? 'bg-purple-100 text-purple-800' : 'bg-red-100 text-red-800'
        }`}>
          {riskLabels[patient.riskLevel]}
        </span>
      </div>
      <div className="mt-2 flex items-center gap-1 text-sm text-orange-700">
        <Clock className="h-3 w-3" />
        <span>{formatExpiredDays(patient.expiredAt)}</span>
      </div>
    </div>
  );
}

interface ActivityItemProps {
  activity: {
    id: string;
    type: string;
    title: string;
    description: string;
    patientName?: string;
    userName: string;
    createdAt: string;
  };
  onClick?: () => void;
}

function ActivityItem({ activity, onClick }: ActivityItemProps) {
  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 60) return `Há ${diffMins} min`;
    if (diffHours < 24) return `Há ${diffHours} hora${diffHours > 1 ? 's' : ''}`;
    return `Há ${diffDays} dia${diffDays > 1 ? 's' : ''}`;
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'CLINICAL_NOTE':
        return <FileText className="h-4 w-4 text-blue-500" />;
      case 'RISK_ASSESSMENT':
        return <AlertTriangle className="h-4 w-4 text-orange-500" />;
      case 'QUESTIONNAIRE':
        return <ClipboardList className="h-4 w-4 text-green-500" />;
      case 'DEMAND':
        return <MessageSquare className="h-4 w-4 text-purple-500" />;
      default:
        return <Activity className="h-4 w-4 text-muted-foreground" />;
    }
  };

  return (
    <div
      className={`flex items-start gap-3 rounded-lg p-3 hover:bg-muted/50 ${onClick ? 'cursor-pointer' : ''}`}
      onClick={onClick}
    >
      <div className="mt-0.5">{getActivityIcon(activity.type)}</div>
      <div className="flex-1 min-w-0">
        <p className="font-medium">{activity.title}</p>
        <p className="truncate text-sm text-muted-foreground">{activity.description}</p>
        <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <User className="h-3 w-3" />
            {activity.userName}
          </span>
          {activity.patientName && (
            <>
              <span>•</span>
              <span>Paciente: {activity.patientName}</span>
            </>
          )}
          <span>•</span>
          <span className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {formatTime(activity.createdAt)}
          </span>
        </div>
      </div>
    </div>
  );
}
